import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import type { MovieItem } from '@/mocks/movies';

interface HeroBannerProps {
  featuredMovies: MovieItem[];
}

export default function HeroBanner({ featuredMovies }: HeroBannerProps) {
  const [current, setCurrent] = useState(0);
  const total = featuredMovies.length;

  const goNext = useCallback(() => {
    setCurrent((prev) => (prev + 1) % total);
  }, [total]);

  useEffect(() => {
    const timer = setInterval(goNext, 5000);
    return () => clearInterval(timer);
  }, [goNext]);

  if (total === 0) return null;

  const movie = featuredMovies[current];

  return (
    <section className="relative w-full h-[420px] md:h-[540px] overflow-hidden">
      {featuredMovies.map((item, index) => (
        <div
          key={item.id}
          className={`absolute inset-0 transition-opacity duration-700 ${
            index === current ? 'opacity-100' : 'opacity-0'
          }`}
        >
          <img
            src={item.bannerImage}
            alt={item.title}
            className="w-full h-full object-cover object-top"
          />
        </div>
      ))}

      <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/20 to-black/70"></div>

      <div className="absolute bottom-0 left-0 right-0 p-6 md:p-12">
        <div className="max-w-3xl">
          <div className="flex items-center gap-3 mb-3">
            <span className="px-2.5 py-1 rounded-full text-xs font-medium bg-primary-500 text-background-50 whitespace-nowrap">
              正在热播
            </span>
            <span className="text-sm text-background-200">{movie.subCategory}</span>
            <span className="text-sm text-background-200">{movie.year}</span>
            <span className="flex items-center gap-1 text-sm text-primary-300">
              <i className="ri-star-fill text-xs"></i>
              {movie.rating}
            </span>
          </div>

          <h2 className="text-2xl md:text-4xl font-heading font-bold text-background-50 mb-3">
            {movie.title}
          </h2>
          <p className="text-sm md:text-base text-background-200 line-clamp-2 mb-4 max-w-xl leading-relaxed">
            {movie.description}
          </p>

          <div className="flex items-center gap-3 flex-wrap">
            <Link
              to={`/detail/${movie.id}`}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-play-circle-fill text-lg"></i>
              查看详情
            </Link>
            <span className="text-xs text-background-300">{movie.updateInfo}</span>
          </div>
        </div>
      </div>

      <div className="absolute bottom-6 right-6 md:right-12 flex items-center gap-2">
        {featuredMovies.map((_, index) => (
          <button
            key={index}
            onClick={() => setCurrent(index)}
            className={`w-2.5 h-2.5 rounded-full transition-all duration-300 cursor-pointer ${
              index === current
                ? 'bg-primary-500 w-6'
                : 'bg-background-400/60 hover:bg-background-300'
            }`}
            aria-label={`第 ${index + 1} 张`}
          />
        ))}
      </div>

      <button
        onClick={() => setCurrent((prev) => (prev - 1 + total) % total)}
        className="absolute left-4 md:left-8 top-1/2 -translate-y-1/2 w-10 h-10 md:w-12 md:h-12 flex items-center justify-center rounded-full bg-background-950/30 hover:bg-background-950/50 text-background-50 transition-all cursor-pointer"
        aria-label="上一个"
      >
        <i className="ri-arrow-left-s-line text-xl"></i>
      </button>
      <button
        onClick={goNext}
        className="absolute right-4 md:right-8 top-1/2 -translate-y-1/2 w-10 h-10 md:w-12 md:h-12 flex items-center justify-center rounded-full bg-background-950/30 hover:bg-background-950/50 text-background-50 transition-all cursor-pointer"
        aria-label="下一个"
      >
        <i className="ri-arrow-right-s-line text-xl"></i>
      </button>
    </section>
  );
}